import {
  FOLDER_ACCESS_MANAGER,
  FOLDER_TYPES,
  FoldersV2,
} from 'lib/api/folders/getFoldersV2';
import { ListDropdownMenu } from 'lib/components';
import { AccessRole } from 'lib/const';
import { useAuth } from 'lib/context';
import {
  checkIfFeatureIsEnabled,
  PackageNameLabel,
  productFeature,
} from 'lib/utils/productFeature';
import React from 'react';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import { MdDeleteForever, MdEdit, MdLock } from 'react-icons/md';
import styled, { useTheme } from 'styled-components/macro';
import selectors from '../../../../../../cypress/selectors';
import { Data } from 'lib/components/ListDropdownMenu';
import { FOLDER_SHARED_AS } from './types';
import { CovideoIcon } from 'lib/components/CovideoIcon';

const CustomListDropdownMenu = styled(ListDropdownMenu)`
  width: 270px;
  right: 0px;
  box-shadow:
    0px 0px 4px ${({ theme }) => theme.colors.secondary[10]},
    0px 6px 12px ${({ theme }) => theme.colors.secondary[5]};
  border-radius: 8px;
  padding: 8px 0px;
`;

const DropdownElement = styled.div`
  position: relative;
  cursor: pointer;
  .show-more-dropdown {
    right: 0;
  }
`;

const IconWrapper = styled.span`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  border: ${({ theme }) => `1px solid ${theme.colors.secondary[5]}`};
  border-radius: 5px;
  svg {
    fill: ${({ theme }) => theme.colors.secondary[100]};
  }
  &:hover {
    background: ${({ theme }) => theme.colors.secondary['non_alpha']};
    border: ${({ theme }) => `1px solid ${theme.colors.secondary[5]}`};
    svg {
      fill: ${({ theme }) => theme.colors.secondary[100]};
    }
  }
`;

const showShareOption = (params: {
  hasNewFoldersAccess: boolean;
  restrictFolderSharingToAdmins: string;
  access: string;
  accessManager?: FOLDER_ACCESS_MANAGER;
}) => {
  const {
    hasNewFoldersAccess,
    restrictFolderSharingToAdmins,
    access,
    accessManager,
  } = params;
  const isAdmin = access === AccessRole.ADMIN;
  const sharingAllowed = accessManager !== FOLDER_ACCESS_MANAGER.NONE;
  const showShareOptionForOldUsers =
    restrictFolderSharingToAdmins?.toString() === '1'
      ? isAdmin
      : sharingAllowed;
  const showShareOptionForNewUsers = isAdmin && sharingAllowed;
  return hasNewFoldersAccess
    ? showShareOptionForNewUsers
    : showShareOptionForOldUsers;
};

type Props = {
  currentFolder: FoldersV2;
  openEditFolderModal: () => void;
  openDeleteFolderModal: () => void;
  handleOpenModal: () => void;
  handleStopSharingClick: () => void;
};
export const FolderActions = ({
  openEditFolderModal,
  openDeleteFolderModal,
  handleOpenModal,
  handleStopSharingClick,
  currentFolder,
}: Props) => {
  const { userData } = useAuth();
  const { colors } = useTheme();
  const [showMoreDropdown, setShowMoreDropdown] = React.useState(false);

  const isFolderSharingEnabled = checkIfFeatureIsEnabled(
    userData,
    productFeature.FOLDER_SHARING
  );

  const showSharingOption = showShareOption({
    hasNewFoldersAccess: !!parseInt(
      userData.customer.hasNewFoldersAccess || '0',
      10
    ),
    restrictFolderSharingToAdmins:
      userData.reseller.restrictFolderSharingToAdmins,
    access: userData.access,
    accessManager: currentFolder.accessManager,
  });

  const showRenameOption = currentFolder.folderType !== FOLDER_TYPES.DEFAULT;
  const showDeleteOption = currentFolder.folderType !== FOLDER_TYPES.DEFAULT;
  const showStopSharingOption =
    currentFolder.folderType === FOLDER_TYPES.SHARED &&
    String(currentFolder?.userId) === userData.userId;
  const showSubtitle = showStopSharingOption && !!userData.isOrganizationAdmin;

  const moreDropdownItems = [
    showRenameOption && {
      title: 'Edit',
      icon: (
        <MdEdit color={colors.neutral[60]} style={{ width: 20, height: 20 }} />
      ),
      onClick: () => openEditFolderModal(),
    },
    showSharingOption && {
      title: 'Share',
      icon: (
        <MdLock color={colors.neutral[60]} style={{ width: 20, height: 20 }} />
      ),
      onClick: () => handleOpenModal(),
      productFeatureId: productFeature.FOLDER_SHARING,
      isDisabled: !isFolderSharingEnabled,
      productTooltipStyle: {
        right: '0px',
        left: '250px',
        top: '77px',
      },
      nextPlan: PackageNameLabel.TEAMS,
      subtitle: showSubtitle
        ? {
            [FOLDER_SHARED_AS.COMPANY_ADMIN]: 'Shared with Org',
            [FOLDER_SHARED_AS.OEM_ADMIN]: 'Shared with OEM',
          }[currentFolder?.folderSharedAs || ''] || ''
        : '',
    },
    showStopSharingOption && {
      title: 'Stop Sharing',
      icon: <CovideoIcon name='remove-circle' />,
      onClick: () => handleStopSharingClick(),
    },
    showDeleteOption && {
      title: 'Delete',
      titleColor: colors.danger[100],
      icon: (
        <MdDeleteForever
          color={colors.danger[100]}
          style={{ width: 20, height: 20 }}
        />
      ),
      onClick: () => openDeleteFolderModal(),
    },
  ].filter(Boolean) as Data[];

  const isFolderOwner = currentFolder.userId?.toString() === userData.userId;
  const isAdmin =
    currentFolder.accessManager === FOLDER_ACCESS_MANAGER.ADMIN &&
    userData.access === AccessRole.ADMIN;

  const showThreeDotMenu =
    !!moreDropdownItems.length &&
    currentFolder &&
    Number(currentFolder?.folderId) > 0 &&
    (isFolderOwner || isAdmin);

  return showThreeDotMenu ? (
    <DropdownElement
      onClick={() => setShowMoreDropdown(true)}
      data-cy={selectors.libraryPage.sharedFolderDropdown}
    >
      <IconWrapper>
        <BiDotsVerticalRounded size={20} color={colors.secondary[40]} />
      </IconWrapper>
      <CustomListDropdownMenu
        isMenuOpen={showMoreDropdown}
        items={moreDropdownItems}
        id={'show-more-dropdown-folder'}
        setIsMenuOpen={() => setShowMoreDropdown(false)}
      />
    </DropdownElement>
  ) : (
    <></>
  );
};
